import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "./config.js";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "600px" };
const defaultCenter = { lat: -17.8274, lng: 31.0435 };

const toNumber = (v) => {
  if (v == null || v === "" || v === "null" || v === "undefined") return null;
  
  // Handle string numbers with potential formatting issues
  if (typeof v === "string") {
    // Remove any non-numeric characters except decimal point, minus sign, and exponent
    const cleaned = v.replace(/[^\d.-eE]/g, "");
    if (cleaned === "" || isNaN(cleaned)) {
      console.warn("Invalid string coordinate:", v, "cleaned:", cleaned);
      return null;
    }
    const n = parseFloat(cleaned);
    const result = Number.isFinite(n) ? n : null;
    console.log("Converted string to number:", v, "→", result);
    return result;
  }
  
  const n = Number(v);
  console.log("Converted to number:", v, "→", n);
  return Number.isFinite(n) ? n : null;
};

const toLatLng = (obj) => {
  if (!obj) return null;
  
  // Handle different coordinate field names and ensure they're valid numbers
  const lat = toNumber(obj.lat ?? obj.latitude ?? obj.lat_cordinates);
  const lng = toNumber(obj.lng ?? obj.longitude ?? obj.long_cordinates ?? obj.lng_cordinates);
  
  if (lat == null || lng == null) {
    console.warn("Invalid coordinates:", { 
      original: obj, 
      processed: { lat, lng },
      reason: lat == null ? "invalid latitude" : "invalid longitude"
    });
    return null;
  }
  
  // Validate coordinate ranges
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    console.warn("Coordinates out of range:", { lat, lng, original: obj });
    return null;
  }
  
  return { lat, lng };
};

const RideShareDriverInTransitRouteTrack = () => {
  console.log("🗺️ [SCREEN] RideShareDriverInTransitRouteTrack - Component Mounted");
  console.log("🗺️ [DEBUG] Starting component initialization...");
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: getGoogleMapsApiKey(),
    libraries,
  });

  // Add error state
  const [error, setError] = useState(null);

  const mapRef = useRef(null);

  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);
  const [midpoints, setMidpoints] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [dropoffs, setDropoffs] = useState([]);

  const [directions, setDirections] = useState(null);
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);

  const center = useMemo(() => {
    const points = [
      origin,
      ...pickups.map((p) => p.point),
      ...midpoints,
      ...dropoffs.map((d) => d.point),
      destination,
    ].filter(Boolean);

    if (points.length === 0) return defaultCenter;
    if (points.length === 1) return points[0];

    // Simple center calculation
    const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
    const avgLng = points.reduce((sum, p) => sum + p.lng, 0) / points.length;
    
    return { lat: avgLat, lng: avgLng };
  }, [origin, destination, pickups, dropoffs, midpoints]);

  const zoom = useMemo(() => {
    const points = [
      origin,
      ...pickups.map((p) => p.point),
      ...midpoints,
      ...dropoffs.map((d) => d.point),
      destination,
    ].filter(Boolean);

    if (points.length < 2) return 12;

    // Calculate max distance between points to determine zoom
    let maxDistance = 0;
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const distance = Math.sqrt(
          Math.pow(points[i].lat - points[j].lat, 2) + 
          Math.pow(points[i].lng - points[j].lng, 2)
        );
        maxDistance = Math.max(maxDistance, distance);
      }
    }

    // Adjust zoom based on max distance
    if (maxDistance > 0.5) return 8;  // Very far apart
    if (maxDistance > 0.2) return 10; // Far apart
    if (maxDistance > 0.1) return 11; // Medium distance
    if (maxDistance > 0.05) return 12; // Close
    return 13; // Very close
  }, [origin, destination, pickups, dropoffs, midpoints]);

  useEffect(() => {
    console.log("🗺️ [DEBUG] Setting up message listeners...");
    
    const handleMessage = (event) => {
      console.log("🗺️ [DEBUG] handleMessage called");
      console.log("🗺️ [DEBUG] Event source:", event.source);
      console.log("🗺️ [DEBUG] Event origin:", event.origin);
      
      try {
        const raw = event?.data;
        console.log("🗺️ [DEBUG] Raw data type:", typeof raw);
        console.log("🗺️ [DEBUG] Raw data exists:", !!raw);
        
        if (!raw) {
          console.log("🗺️ [DEBUG] No raw data, returning early");
          return;
        }
        
        console.log("🗺️ [MAPS] Raw message received:", typeof raw, raw?.substring?.(0, 200));
        
        console.log("🗺️ [DEBUG] Parsing payload...");
        const payload = typeof raw === "string" ? JSON.parse(raw) : raw;
        console.log("🗺️ [MAPS] Parsed payload keys:", Object.keys(payload));
        console.log("🗺️ [MAPS] Trip coords:", payload?.tripCoords);
        console.log("🗺️ [DEBUG] Payload structure:", {
          hasTrip: !!payload?.trip,
          hasTripDetails: !!payload?.tripDetails,
          hasAcceptedRequests: !!payload?.acceptedRequests,
          hasRequests: !!payload?.requests,
          hasMidpoints: !!payload?.midpoints,
          acceptedRequestsType: typeof payload?.acceptedRequests,
          requestsType: typeof payload?.requests
        });

        console.log("🗺️ [DEBUG] Extracting trip data...");
        const trip = payload?.trip ?? payload?.tripDetails ?? payload;
        console.log("🗺️ [DEBUG] Trip source:", payload?.trip ? 'payload.trip' : payload?.tripDetails ? 'payload.tripDetails' : 'payload');
        
        console.log("🗺️ [DEBUG] Processing accepted requests...");
        const accepted = Array.isArray(payload?.acceptedRequests)
          ? payload.acceptedRequests
          : Array.isArray(payload?.requests)
            ? payload.requests
            : [];
        console.log("🗺️ [DEBUG] Accepted requests source:", payload?.acceptedRequests ? 'payload.acceptedRequests' : payload?.requests ? 'payload.requests' : 'empty array');

        console.log("🗺️ [MAPS] Trip data:", trip);
        console.log("🗺️ [MAPS] Accepted requests count:", accepted.length);
        console.log("🗺️ [DEBUG] Trip coordinates check:", {
          origin_lat: trip?.origin_lat,
          origin_lng: trip?.origin_lng,
          destination_lat: trip?.destination_lat,
          destination_lng: trip?.destination_lng
        });
        console.log("🗺️ [DEBUG] First accepted request:", accepted[0]);

        console.log("🗺️ [DEBUG] Starting coordinate extraction...");

        // Enhanced coordinate extraction with validation
        console.log("🗺️ [DEBUG] Extracting origin coordinates...");
        const o = toLatLng({ 
          lat: trip?.origin_lat, 
          lng: trip?.origin_lng,
          latitude: trip?.origin_lat,
          longitude: trip?.origin_lng
        });
        console.log("🗺️ [DEBUG] Origin extraction result:", o);
        
        console.log("🗺️ [DEBUG] Extracting destination coordinates...");
        const d = toLatLng({ 
          lat: trip?.destination_lat, 
          lng: trip?.destination_lng,
          latitude: trip?.destination_lat,
          longitude: trip?.destination_lng
        });
        console.log("🗺️ [DEBUG] Destination extraction result:", d);

        console.log("🗺️ [MAPS] Processed coordinates - Origin:", o, "Destination:", d);
        console.log("🗺️ [DEBUG] Processing midpoints...");

        const mids = Array.isArray(payload?.midpoints)
          ? payload.midpoints
          : Array.isArray(trip?.midpoints)
            ? trip.midpoints
            : [];
        console.log("🗺️ [DEBUG] Midpoints source:", payload?.midpoints ? 'payload.midpoints' : trip?.midpoints ? 'trip.midpoints' : 'empty array');
        console.log("🗺️ [DEBUG] Raw midpoints:", mids);

        const midPts = (Array.isArray(mids) ? mids : [])
          .map((m) => toLatLng({ 
            lat: m?.lat ?? m?.latitude, 
            lng: m?.lng ?? m?.longitude,
            latitude: m?.lat,
            longitude: m?.lng
          }))
          .filter(Boolean);

        console.log("🗺️ [MAPS] Midpoints processed:", midPts.length);

        console.log("🗺️ [DEBUG] Processing pickups...");
        const pu = (Array.isArray(accepted) ? accepted : [])
          .map((r, index) => {
            console.log(`🗺️ [DEBUG] Processing pickup ${index}:`, r);
            return {
              request_id: r?.request_id,
              point: toLatLng({ 
                lat: r?.pickup_lat, 
                lng: r?.pickup_lng,
                latitude: r?.pickup_lat,
                longitude: r?.pickup_lng
              })
            };
          })
          .filter((x) => x.point);
        console.log("🗺️ [DEBUG] Processed pickups:", pu);

        console.log("🗺️ [DEBUG] Processing dropoffs...");
        const doff = (Array.isArray(accepted) ? accepted : [])
          .map((r, index) => {
            console.log(`🗺️ [DEBUG] Processing dropoff ${index}:`, r);
            return {
              request_id: r?.request_id,
              point: toLatLng({ 
                lat: r?.dropoff_lat, 
                lng: r?.dropoff_lng,
                latitude: r?.dropoff_lat,
                longitude: r?.dropoff_lng
              })
            };
          })
          .filter((x) => x.point);
        console.log("🗺️ [DEBUG] Processed dropoffs:", doff);

        console.log("🗺️ [MAPS] Pickups:", pu.length, "Dropoffs:", doff.length);
        console.log("🗺️ [DEBUG] Final data summary:", {
          origin: o,
          destination: d,
          midpointsCount: midPts.length,
          pickupsCount: pu.length,
          dropoffsCount: doff.length,
          hasValidOrigin: !!o,
          hasValidDestination: !!d
        });

        console.log("🗺️ [DEBUG] Updating component state...");

        // Only update state if we have valid coordinates
        if (o) {
          console.log("🗺️ [DEBUG] Setting origin:", o);
          setOrigin(o);
        } else {
          console.log("🗺️ [DEBUG] No valid origin, not setting origin state");
        }
        
        if (d) {
          console.log("🗺️ [DEBUG] Setting destination:", d);
          setDestination(d);
        } else {
          console.log("🗺️ [DEBUG] No valid destination, not setting destination state");
        }
        
        console.log("🗺️ [DEBUG] Setting midpoints:", midPts.length);
        setMidpoints(midPts);
        console.log("🗺️ [DEBUG] Setting pickups:", pu.length);
        setPickups(pu);
        console.log("🗺️ [DEBUG] Setting dropoffs:", doff.length);
        setDropoffs(doff);
        
        console.log("🗺️ [MAPS] State updated successfully");
      } catch (e) {
        console.error("💥 [ERROR] Maps component - message processing failed:", e);
        console.error("💥 [ERROR] Message data:", event?.data);
        setError("Failed to process map data");
        return;
      }
    };

    console.log("🗺️ [DEBUG] Message listeners setup complete");
    window.addEventListener("message", handleMessage);
    document.addEventListener("message", handleMessage);
    console.log("🗺️ [DEBUG] Event listeners added");

    return () => {
      console.log("🗺️ [DEBUG] Cleaning up message listeners...");
      window.removeEventListener("message", handleMessage);
      document.removeEventListener("message", handleMessage);
      console.log("🗺️ [DEBUG] Event listeners removed");
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !window.google) return;
    if (!origin || !destination) return;

    const points = [
      origin,
      ...pickups.map((p) => p.point),
      ...midpoints,
      ...dropoffs.map((d) => d.point),
      destination,
    ].filter(Boolean);

    const limited = points.slice(0, 10);
    if (limited.length < 2) return;

    const service = new window.google.maps.DirectionsService();
    const o = limited[0];
    const d = limited[limited.length - 1];
    const waypoints = limited.slice(1, limited.length - 1).map((p) => ({
      location: p,
      stopover: true,
    }));

    service.route(
      {
        origin: o,
        destination: d,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: false,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          setDirections(null);
        }
      }
    );
  }, [isLoaded, origin, destination, pickups, dropoffs, midpoints]);

  if (loadError) return <div>Error loading Google Maps</div>;
  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div style={{ maxWidth: "900px", margin: "auto" }}>
      <style>
        {`
          .map-legend {
            background: rgba(255, 255, 255, 0.95) !important;
            border: 1px solid #ddd !important;
            border-radius: 8px !important;
            padding: 12px !important;
            margin: 10px 0 !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
            position: fixed !important;
            top: 20px !important;
            right: 20px !important;
            z-index: 1000 !important;
            max-width: 250px !important;
            min-width: 200px !important;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .map-legend.collapsed {
            transform: translateX(270px) !important;
          }
          .legend-toggle {
            background: #1976D2 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            width: 40px !important;
            height: 40px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            cursor: pointer !important;
            font-size: 18px !important;
            font-weight: bold !important;
            box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3) !important;
            transition: all 0.2s ease !important;
            position: absolute !important;
            left: -50px !important;
            top: 0 !important;
          }
          .legend-toggle:hover {
            background: #1565C0 !important;
            transform: scale(1.05) !important;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4) !important;
          }
          .legend-toggle:active {
            transform: scale(0.95) !important;
          }
          .legend-content {
            opacity: 1 !important;
            transition: opacity 0.3s ease !important;
          }
          .map-legend.collapsed .legend-content {
            opacity: 0 !important;
            pointer-events: none !important;
          }
          .legend-title {
            font-size: 16px !important;
            font-weight: bold !important;
            margin-bottom: 8px !important;
            color: #333 !important;
          }
          .legend-items {
            display: flex !important;
            flex-wrap: wrap !important;
            gap: 12px !important;
          }
          .legend-item {
            display: flex !important;
            align-items: center !important;
            gap: 6px !important;
          }
          .legend-icon {
            width: 20px !important;
            height: 20px !important;
            border-radius: 50% !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 10px !important;
            font-weight: bold !important;
            color: white !important;
          }
          .legend-text {
            font-size: 12px !important;
            color: #555 !important;
          }
        `}
      </style>
      <h2>Driver In-Transit Tracking</h2>
      
      {/* Map Legend */}
      <div className={`map-legend ${isLegendCollapsed ? 'collapsed' : ''}`}>
        <button 
          className="legend-toggle" 
          onClick={() => setIsLegendCollapsed(!isLegendCollapsed)}
          title={isLegendCollapsed ? "Show Legend" : "Hide Legend"}
        >
          {isLegendCollapsed ? '◀' : '▶'}
        </button>
        
        <div className="legend-content">
          <div className="legend-title">📍 Map Legend</div>
          <div className="legend-items">
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#2E7D32" }}>●</div>
              <div className="legend-text">START - Trip Beginning</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#1976D2" }}>●</div>
              <div className="legend-text">PICKUPS - Passenger Locations</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#F57C00" }}>●</div>
              <div className="legend-text">DROPOFFS - Exit Points</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#7B1FA2" }}>●</div>
              <div className="legend-text">STOPS - Intermediate Points</div>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ background: "#C62828" }}>●</div>
              <div className="legend-text">DESTINATION - Final Point</div>
            </div>
          </div>
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={zoom}
        center={center}
        onLoad={(map) => (mapRef.current = map)}
        options={{
          gestureHandling: "auto",
          scrollwheel: true,
          zoomControl: true,
          panControl: true,
          scaleControl: true,
        }}
      >
        {origin && (
          <Marker 
            position={origin} 
            title="Trip Starting Point"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#2E7D32",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}

        {pickups.map((p, idx) => (
          <Marker
            key={`pickup-${p.request_id ?? idx}`}
            position={p.point}
            title={`Pickup ${idx + 1}`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#1976D2",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        ))}

        {dropoffs.map((d, idx) => (
          <Marker
            key={`dropoff-${d.request_id ?? idx}`}
            position={d.point}
            title={`Dropoff ${idx + 1}`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#F57C00",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        ))}

        {midpoints.map((m, idx) => (
          <Marker 
            key={`mid-${idx}`} 
            position={m} 
            title={`Stop ${idx + 1} on Route`}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: "#7B1FA2",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        ))}

        {destination && (
          <Marker 
            position={destination} 
            title="Trip Destination"
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 14,
              fillColor: "#C62828",
              fillOpacity: 1.0,
              strokeColor: "#FFFFFF",
              strokeWeight: 3,
            }}
          />
        )}

        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              polylineOptions: { strokeColor: "#7B1FA2", strokeWeight: 5, strokeOpacity: 0.85 },
              preserveViewport: true,
              draggable: false,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default RideShareDriverInTransitRouteTrack;
